import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Autocomplete, FormControlLabel, TextField } from '@mui/material';
import { isBoolean } from 'lodash';
import { WbsCodeTypeEnum } from '~/api/types';
import { useGetDatasetQuery, useGetWbsCodeQuery, useGetWbsCodesQuery, useGetСostCenterQuery } from '~/api/v2/hooks';
import { useHasLockedStatus } from '~/hooks/dataset';

const WbsCodeField = () => {
  const { control, watch, setError, setValue, trigger } = useFormContext();
  const datasetId = +useParams().id!;
  const projectId = +useParams().projectid!;
  const wbsCodeId = watch('wbs_code_id');
  const costCenterId = watch('cost_center_id');
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const { data } = useGetDatasetQuery({ datasetId }, { skip: !datasetId });
  const effectiveWbsCodeId = wbsCodeId || data?.wbs_code_id;
  const effectiveCostCenterId = costCenterId || data?.cost_center_id;
  const { currentData, isFetching } = useGetWbsCodesQuery({ search: searchQuery });
  const { data: wbsCode } = useGetWbsCodeQuery({ id: effectiveWbsCodeId }, { skip: !effectiveWbsCodeId });
  const { data: costCenter } = useGetСostCenterQuery({ id: effectiveCostCenterId }, { skip: !effectiveCostCenterId });
  const { hasLockedStatus } = useHasLockedStatus();

  useEffect(() => {
    if (!(datasetId || projectId) || hasLockedStatus) return;

    if (!effectiveWbsCodeId && !effectiveCostCenterId) {
      setError('wbs_code_id', { message: 'WBS or cost center code is required.' });
    } else if (isBoolean(wbsCode?.wbs_code_is_active) && !wbsCode?.wbs_code_is_active && effectiveWbsCodeId) {
      setError('wbs_code_id', { message: 'The selected WBS code is no longer active.' });
    } else if (
      isBoolean(costCenter?.cost_center_is_active) &&
      !costCenter?.cost_center_is_active &&
      effectiveCostCenterId
    ) {
      setError('wbs_code_id', { message: 'The selected cost center code is no longer active.' });
    }
  }, [wbsCode?.wbs_code_is_active, costCenter?.cost_center_is_active, hasLockedStatus, data]);

  useEffect(() => {
    if (wbsCode && !inputValue) setInputValue(wbsCode.wbs_element_displ_id);
  }, [wbsCode]);

  useEffect(() => {
    if (costCenter && !inputValue) setInputValue(costCenter.cost_center_id);
  }, [costCenter]);

  return (
    <FormControlLabel
      label="WBS / COST CENTER CODE"
      required
      control={
        <Controller
          name="wbs_code_id"
          control={control}
          render={({ field: { value }, fieldState }) => {
            const activeId = value ?? costCenterId;
            const resolvedValue =
              currentData?.find(({ id }) => id === activeId) ??
              (wbsCodeId && wbsCode
                ? { id: wbsCode.id, verbose_name: wbsCode.wbs_element_displ_id, type: WbsCodeTypeEnum.WBS_CODE }
                : null) ??
              (costCenterId && costCenter
                ? {
                    id: costCenter.id,
                    verbose_name: costCenter.cost_center_id,
                    type: WbsCodeTypeEnum.COST_CENTER,
                  }
                : null) ??
              null;

            const handleChange = (_: unknown, selected: typeof resolvedValue) => {
              if (selected?.type === WbsCodeTypeEnum.COST_CENTER) {
                setValue('wbs_code_id', null, { shouldValidate: false, shouldDirty: true });
                setValue('cost_center_id', selected.id, { shouldValidate: false, shouldDirty: true });
              } else {
                setValue('cost_center_id', null, { shouldValidate: false, shouldDirty: true });
                setValue('wbs_code_id', selected?.id ?? null, { shouldValidate: false, shouldDirty: true });
              }
              setInputValue(selected?.verbose_name ?? '');
              trigger('wbs_code_id');
            };

            const handleInputChange = (_: unknown, newValue: string, reason: string) => {
              if (reason === 'input') {
                setInputValue(newValue);
                setSearchQuery(newValue);
              }
            };

            return (
              <Autocomplete
                options={currentData ?? []}
                getOptionLabel={({ verbose_name, type }) =>
                  `${verbose_name} (${type === WbsCodeTypeEnum.WBS_CODE ? 'WBS' : 'CC'})`
                }
                getOptionKey={(option) => option.id}
                isOptionEqualToValue={(option, val) => option.id === val.id}
                value={resolvedValue}
                inputValue={inputValue}
                disableClearable={!inputValue}
                open={open}
                loading={isFetching}
                disabled={hasLockedStatus}
                filterOptions={(options) => options}
                onChange={handleChange}
                onInputChange={handleInputChange}
                onOpen={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onClose={(_, reason) => {
                  if (reason !== 'blur') setOpen(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Type a WBS or cost center code to search"
                    error={!!fieldState.error}
                    helperText={
                      (fieldState.error?.message as string) ||
                      (!inputValue ? 'Start typing to narrow down the results' : '')
                    }
                  />
                )}
              />
            );
          }}
        />
      }
    />
  );
};

export default WbsCodeField;
